// Mono — the machine-data wrapper, pure.
//
// Wraps machine data — IDs, citation locators, model names, token counts,
// timestamps, short codes, export/code fragments — in the mono face
// (foundations §2.5). Its job is a TRUST SIGNAL: rendering the machine's
// bookkeeping in monospace visually splits it from the authored prose set in
// sans, so an author can see at a glance what is "the work" versus "the
// system's data". Not decoration — a reliability cue in an AI tool.
//
// Design choices encoded here:
//   • DISTINCT typed props, no anonymous shapes — `as` and `tone` are each a
//     small named union discriminated by their literal value; the public
//     surface is `MonoProps`, nothing else leaks.
//   • `as` chooses the element WITHOUT changing meaning — a plain union, not a
//     discriminated variant. "code" carries HTML code semantics (a code
//     fragment / identifier), "span" is the neutral inline default for a value
//     that is data but not literally code. `createElement` renders the chosen
//     tag with no `as`-cast (both are valid intrinsic tags).
//   • `tone` is a TYPOGRAPHIC lever (how loud the data sits on the ground),
//     not a state signal — machine data is bookkeeping, so it defaults to
//     `dim`, quieter than the surrounding prose. No colour carries meaning
//     here, so SC 1.4.1 is not in play.
//
// Pure/props-driven: `as` picks the tag, `tone` picks the weight-on-page,
// `children` is the data; every other attribute (title, id, aria-*) passes
// straight through. No side effects, no state. Nothing reaches out.

import { createElement, type HTMLAttributes, type ReactNode } from "react";
import "./Mono.css";

// The elements Mono is allowed to become. Both are valid intrinsic tags, so
// the union renders with no cast: "code" for a literal code fragment /
// identifier, "span" for a data value that is not itself code.
export type MonoElement = "span" | "code";
// How loud the data reads: `dim` (default) is quieter than prose; `faint` is
// the lowest legible floor for the least important bookkeeping; `default`
// matches full foreground when the value itself is the focus.
type MonoTone = "default" | "dim" | "faint";

export type MonoProps = HTMLAttributes<HTMLElement> & {
    readonly as?: MonoElement;
    readonly tone?: MonoTone;
    readonly children?: ReactNode;
};

export const Mono = ({
    as = "span",
    tone = "dim",
    className,
    children,
    ...rest
}: MonoProps) =>
    createElement(
        as,
        {
            className: `bilda-mono bilda-mono--${tone}${
                className ? ` ${className}` : ""
            }`,
            ...rest,
        },
        children,
    );
